import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ShieldCheck,
    Shield,
    Star,
    Upload,
    FileText,
    AlertCircle,
    CheckCircle,
    Clock,
    XCircle,
    Loader2,
    ArrowLeft,
    RefreshCw,
    ChevronRight,
    Award
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { cn } from '../lib/utils';

// Tier badge component
const TierBadge = ({ tier, size = "default" }) => {
    const tiers = {
        basic: { 
            label: 'Basic', 
            color: 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border-gray-500/30',
            icon: Shield
        },
        verified: { 
            label: 'Verified', 
            color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
            icon: ShieldCheck
        },
        trusted: { 
            label: 'Trusted', 
            color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
            icon: Star
        },
    };
    
    const t = tiers[tier] || tiers.basic;
    const Icon = t.icon;
    const sizeClasses = size === "large" 
        ? "px-4 py-2 text-base gap-2"
        : "px-3 py-1.5 text-sm gap-1.5";
    const iconSize = size === "large" ? "h-5 w-5" : "h-4 w-4";
    
    return (
        <span className={cn(
            "inline-flex items-center font-medium rounded-full border",
            t.color,
            sizeClasses
        )}>
            <Icon className={iconSize} />
            {t.label}
        </span>
    );
};

// Status badge for submissions
const StatusBadge = ({ status }) => {
    const statuses = {
        pending: { label: 'Pending Review', color: 'bg-amber-500/15 text-amber-600', icon: Clock },
        approved: { label: 'Approved', color: 'bg-emerald-500/15 text-emerald-600', icon: CheckCircle },
        rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-600', icon: XCircle },
        resubmission_requested: { label: 'Resubmission Needed', color: 'bg-orange-500/15 text-orange-600', icon: RefreshCw },
        revoked: { label: 'Revoked', color: 'bg-gray-500/15 text-gray-600', icon: XCircle },
    };
    
    const s = statuses[status] || statuses.pending;
    const Icon = s.icon;
    
    return (
        <span className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full",
            s.color
        )}>
            <Icon className="h-3.5 w-3.5" />
            {s.label}
        </span>
    );
};

// Document submission card
const SubmissionCard = ({ submission }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="p-4 bg-accent/30 rounded-lg border border-border">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10">
                        <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium text-foreground">{submission.type_label}</p>
                        <p className="text-xs text-muted-foreground">
                            Submitted {formatDate(submission.submitted_at)}
                        </p>
                    </div>
                </div>
                <StatusBadge status={submission.status} />
            </div>
            
            {submission.status === 'rejected' && submission.rejection_reason && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">
                        <AlertCircle className="h-4 w-4 inline mr-1" />
                        {submission.rejection_reason}
                    </p>
                </div>
            )}
            
            {submission.status === 'resubmission_requested' && (
                <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                        <RefreshCw className="h-4 w-4 inline mr-1" />
                        {submission.rejection_reason || 'Please resubmit a clearer document'}
                    </p>
                </div>
            )}
            
            {submission.reviewed_at && submission.status !== 'pending' && (
                <p className="text-xs text-muted-foreground mt-2">
                    Reviewed {formatDate(submission.reviewed_at)}
                </p>
            )}
        </div>
    );
};

const VerificationSettings = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState(null);
    const [selectedType, setSelectedType] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [expirationDate, setExpirationDate] = useState('');

    useEffect(() => {
        loadVerificationStatus();
    }, []);

    const loadVerificationStatus = async () => {
        try {
            const response = await api.get('/verification/status');
            setStatus(response.data);
        } catch (err) {
            console.error('Failed to load verification status:', err);
            toast.error('Failed to load verification status');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Please upload a PDF, JPG, or PNG file');
                return;
            }
            // Validate file size (10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File must be under 10MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleSubmit = async () => {
        if (!selectedType) {
            toast.error('Please select a document type');
            return;
        }
        if (!selectedFile) {
            toast.error('Please select a file to upload');
            return;
        }

        // Check for pending submission of same type
        const hasPending = status?.submissions?.some(
            s => s.type === selectedType && s.status === 'pending'
        );
        if (hasPending) {
            toast.error('You already have a pending submission for this type');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('document', selectedFile);
            formData.append('verification_type', selectedType);
            if (expirationDate) {
                formData.append('expiration_date', expirationDate);
            }

            const token = localStorage.getItem('clientcheck_token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verification/submit`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to submit document');
            }

            toast.success('Document submitted for review');
            setSelectedType('');
            setSelectedFile(null);
            setExpirationDate('');
            // Refresh status
            loadVerificationStatus();
        } catch (err) {
            console.error('Upload error:', err);
            toast.error(err.message || 'Failed to submit document');
        } finally {
            setUploading(false);
        }
    };

    // Calculate tier progress
    const getTierProgress = () => {
        if (!status) return 0;
        if (status.current_tier === 'trusted') return 100;
        if (status.current_tier === 'verified') return 66;
        return 33;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6">
                {/* Header */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/settings')}
                    className="mb-4 -ml-2"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Settings
                </Button>

                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 rounded-lg bg-primary/10">
                        <Award className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                            Contractor Verification
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Verify your credentials to increase your review weight
                        </p>
                    </div>
                </div>

                {/* Current Tier Card */}
                <Card className="bg-card border-border mb-6">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Current Status</p>
                                <TierBadge tier={status?.current_tier} size="large" />
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-muted-foreground mb-1">Review Weight</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {(status?.weight_multiplier || 0.9).toFixed(2)}x
                                </p>
                            </div>
                        </div>

                        {/* Tier Progress */}
                        <div className="mt-4">
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                                <span>Basic</span>
                                <span>Verified</span>
                                <span>Trusted</span>
                            </div>
                            <Progress value={getTierProgress()} className="h-2" />
                        </div>

                        {/* Next Tier Requirements */}
                        {status?.next_tier && status?.missing_requirements?.length > 0 && (
                            <div className="mt-4 p-4 bg-accent/30 rounded-lg border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <ChevronRight className="h-4 w-4 text-primary" />
                                    <p className="text-sm font-medium text-foreground">
                                        Next: {status.next_tier_name}
                                    </p>
                                </div>
                                <ul className="space-y-1">
                                    {status.missing_requirements.map((req, i) => (
                                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                            <span className="text-primary">•</span>
                                            {req}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Document Upload Card */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Upload className="h-5 w-5 text-primary" />
                            Submit Verification Document
                        </CardTitle>
                        <CardDescription>
                            Upload your license, insurance, bond, or business registration.
                            Documents are private and visible to admins only.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Document Type */}
                        <div className="space-y-2">
                            <Label htmlFor="doc-type">Document Type</Label>
                            <Select value={selectedType} onValueChange={setSelectedType}>
                                <SelectTrigger id="doc-type" data-testid="doc-type-select">
                                    <SelectValue placeholder="Select document type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {status?.verification_types?.map((type) => (
                                        <SelectItem key={type.key} value={type.key}>
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* File Upload */}
                        <div className="space-y-2">
                            <Label htmlFor="doc-file">Document File</Label>
                            <div className="flex items-center gap-3">
                                <Input
                                    id="doc-file"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={handleFileSelect}
                                    className="flex-1"
                                    data-testid="doc-file-input"
                                />
                            </div>
                            {selectedFile && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                                Accepted formats: PDF, JPG, PNG (max 10MB)
                            </p>
                        </div>

                        {/* Expiration Date (Optional) */}
                        <div className="space-y-2">
                            <Label htmlFor="expiration">Expiration Date (Optional)</Label>
                            <Input
                                id="expiration"
                                type="date"
                                value={expirationDate}
                                onChange={(e) => setExpirationDate(e.target.value)}
                                className="max-w-xs"
                                data-testid="expiration-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                If your document has an expiration date, enter it here
                            </p>
                        </div>

                        {/* Submit Button */}
                        <Button 
                            onClick={handleSubmit}
                            disabled={uploading || !selectedType || !selectedFile}
                            className="w-full"
                            data-testid="submit-verification-btn"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Submit for Review
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>

                {/* Submission History */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="h-5 w-5 text-primary" />
                            Your Submissions
                        </CardTitle>
                        <CardDescription>
                            Track the status of your verification documents
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {status?.submissions?.length > 0 ? (
                            <div className="space-y-3">
                                {status.submissions.map((submission) => (
                                    <SubmissionCard key={submission.id} submission={submission} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground mb-2">No submissions yet</p>
                                <p className="text-xs text-muted-foreground">
                                    Upload your first document to get verified
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="bg-primary/5 border-primary/20 mt-6">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-foreground mb-1">About Verification</p>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                    <li>• Verified contractors have more influence on property scores</li>
                                    <li>• Documents are reviewed within 48 hours</li>
                                    <li>• All documents are stored securely and only visible to admins</li>
                                    <li>• Trusted status requires 10+ approved reviews</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default VerificationSettings;
